import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { usePageMeta } from '@/hooks/useSEO';
import {
  ArrowRight, ArrowUpRight, Globe2, Shield, BadgeCheck,
  GraduationCap, CheckCircle2, Sparkles, LineChart, MapPin, Building2, Handshake,
} from 'lucide-react';

/**
 * AboutZafcoStudyAbroadPage — `/study-abroad/about-zafco/`
 * Enterprise-grade About page: Hero → Credibility (MEA badge) → Leadership grid
 * → Geographic footprint → Core pillars → Promise box → Final CTA.
 */

const MEA_RC = 'B-0762/DEL/PER/1000+/5/9148/2015';

const COUNTRIES = [
  { flag: '🇬🇧', name: 'UK' },
  { flag: '🇨🇦', name: 'Canada' },
  { flag: '🇦🇺', name: 'Australia' },
  { flag: '🇮🇪', name: 'Ireland' },
  { flag: '🇦🇪', name: 'Dubai' },
];

const CREDIBILITY_STATS = [
  { value: '15+', label: 'Years in Global Mobility' },
  { value: '10,000+', label: 'Successful Placements' },
  { value: '50+', label: 'Destination Countries' },
  { value: '2015', label: 'MEA Approved Since' },
];

const LEADERSHIP = [
  {
    initials: 'AWZ',
    name: 'Mr. Abdul Wahhab Zafar',
    title: 'Chairman & Managing Partner',
    body: 'The founding force behind Zafco Global Mobility. With over a decade of experience moving professionals, families, and entrepreneurs across borders, he leads the group\u2019s vision of turning study abroad into full career migration.',
  },
  {
    initials: 'AI',
    name: 'Mr. Ahmad Iqbal',
    title: 'Partner',
    body: 'Oversees operations, compliance, and destination partnerships. His mandate: every student file must satisfy immigration policy, employer expectations, and long-term residency logic \u2014 not just admission criteria.',
  },
  {
    initials: 'GA',
    name: 'Mrs. Gulfishan Afza',
    title: 'Partner',
    body: 'Champions the student and family experience \u2014 from the first counselling session to post-landing support. She ensures every roadmap is transparent, realistic, and aligned with each family\u2019s budget and goals.',
  },
];

const FOOTPRINT = [
  {
    Icon: Building2,
    region: 'New Delhi, India',
    tag: 'Global Headquarters',
    body: 'Our command centre in New Friends Colony, New Delhi \u2014 home to 50+ immigration specialists handling counselling, documentation, visa filing, and CRM-backed case tracking.',
  },
  {
    Icon: Globe2,
    region: 'Middle East & Gulf',
    tag: 'MEA-Licensed Corridor',
    body: 'Active recruitment and mobility corridors across Saudi Arabia, UAE, and the wider Gulf \u2014 the same compliance muscle now powering our Dubai study pathway and Golden Visa guidance.',
  },
  {
    Icon: MapPin,
    region: 'UK · Canada · Australia · Ireland',
    tag: 'Study Destinations',
    body: 'University and institutional networks across all four major English-speaking destinations, evaluated continuously on visa stability, work rights, and PR frameworks \u2014 not commissions.',
  },
];

const CORE_PILLARS = [
  {
    Icon: Globe2,
    title: 'Global Mobility Expertise',
    body: 'Every country, course, and city is evaluated not only for rankings, but for visa stability, work rights, and long\u2011term residency options.',
  },
  {
    Icon: GraduationCap,
    title: 'End\u2011to\u2011End Student Support',
    body: 'From shortlisting and tests to visa filing, post\u2011landing setup, and early\u2011career guidance, the same team stays with you across milestones.',
  },
  {
    Icon: LineChart,
    title: 'Career\u2011First Approach',
    body: 'Recommendations are built on labour\u2011market data, skills shortages, salary bands, and PR frameworks \u2014 not on which university pays the highest commission.',
  },
];

const PROMISE_POINTS = [
  'No fake offers, \u201cguaranteed visa\u201d claims, or forced university tie\u2011ups.',
  'Transparent discussions on budget, return on investment, and realistic timelines.',
  'Honest feedback when a course, college, or country does not align with your long\u2011term plans.',
];

const SectionHeading = ({ eyebrow, title, subtitle, light, center }) => (
  <div className={`max-w-3xl mb-8 lg:mb-12 ${light ? 'text-white' : ''} ${center ? 'mx-auto text-center' : ''}`}>
    {eyebrow && (
      <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full mb-3 ${
        light
          ? 'bg-[#C5A028]/15 border border-[#C5A028]/35 text-[#FACC15]'
          : 'bg-[#C5A028]/12 border border-[#C5A028]/35 text-[#C5A028]'
      } text-[10px] font-bold tracking-[0.22em] uppercase`}>
        {eyebrow}
      </div>
    )}
    <h2 className={`text-2xl sm:text-3xl lg:text-[36px] font-bold font-syne leading-tight mb-3 ${
      light ? 'text-white' : 'text-[#003087]'
    }`}>
      {title}
    </h2>
    {subtitle && (
      <p className={`leading-relaxed text-sm sm:text-base ${
        light ? 'text-slate-300' : 'text-slate-600'
      }`}>
        {subtitle}
      </p>
    )}
  </div>
);

const AboutZafcoStudyAbroadPage = () => {
  usePageMeta(
    'About Zafco Study Abroad | Global Mobility & Career Migration Experts',
    'Discover why Zafco is more than just a study abroad agency. Govt of India MEA-approved, led by an experienced leadership team, delivering data-driven career migration strategies for the UK, Canada, Australia, Ireland, and Dubai.',
    { canonical: '/study-abroad/about-zafco/' },
  );

  // BreadcrumbList + AboutPage JSON-LD — auto-inject + cleanup on unmount
  useEffect(() => {
    const breadcrumb = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home',         item: 'https://zafco.in/' },
        { '@type': 'ListItem', position: 2, name: 'Study Abroad', item: 'https://zafco.in/study-abroad/' },
        { '@type': 'ListItem', position: 3, name: 'About Zafco Study Abroad' },
      ],
    };
    const aboutOrg = {
      '@context': 'https://schema.org',
      '@type': 'AboutPage',
      '@id': 'https://zafco.in/study-abroad/about-zafco/#webpage',
      url: 'https://zafco.in/study-abroad/about-zafco/',
      name: 'About Zafco Study Abroad',
      isPartOf: { '@id': 'https://zafco.in/#website' },
      about: { '@id': 'https://zafco.in/#organization' },
      mainEntity: { '@id': 'https://zafco.in/#organization' },
      inLanguage: 'en-IN',
      description:
        'Student-focused arm of Zafco Global Mobility — a Government of India MEA-approved recruitment agency. Delivers structured, data-driven career migration strategies for Indian students targeting the UK, Canada, Australia, Ireland, and Dubai.',
    };
    const bcEl  = document.createElement('script');
    const aboutEl = document.createElement('script');
    bcEl.type   = 'application/ld+json'; bcEl.setAttribute('data-az-bc-schema', '1');    bcEl.text   = JSON.stringify(breadcrumb);
    aboutEl.type = 'application/ld+json'; aboutEl.setAttribute('data-az-about-schema', '1'); aboutEl.text = JSON.stringify(aboutOrg);
    document.head.appendChild(bcEl);
    document.head.appendChild(aboutEl);
    return () => {
      document.head.querySelector('script[data-az-bc-schema]')?.remove();
      document.head.querySelector('script[data-az-about-schema]')?.remove();
    };
  }, []);

  return (
    <div className="bg-white zaf-prose" data-testid="az-page">
      {/* ============ 1. HERO ============ */}
      <section
        className="relative overflow-hidden text-white"
        style={{ background: 'linear-gradient(135deg, #003087 0%, #001F5C 55%, #020617 100%)' }}
      >
        <div aria-hidden="true" className="pointer-events-none absolute -top-32 -right-24 w-96 h-96 rounded-full bg-[#C5A028]/14 blur-3xl" />
        <div aria-hidden="true" className="pointer-events-none absolute -bottom-32 -left-20 w-80 h-80 rounded-full bg-emerald-500/8 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-6 py-16 lg:py-28">
          <div className="max-w-3xl">
            <div className="flex flex-wrap items-center gap-3 mb-5">
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/8 border border-white/15 backdrop-blur-sm text-[10.5px] font-bold tracking-[0.22em] text-[#FACC15] uppercase">
                <Sparkles className="w-3.5 h-3.5" /> About · Zafco Study Abroad
              </span>
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/15 border border-emerald-400/40 text-[10.5px] font-bold tracking-[0.22em] text-emerald-200 uppercase">
                <Shield className="w-3.5 h-3.5" /> Govt of India · MEA Approved
              </span>
            </div>
            <h1
              className="text-3xl sm:text-4xl lg:text-[54px] font-bold font-syne leading-[1.1] tracking-tight mb-5"
              data-testid="az-h1"
            >
              About Zafco Study Abroad: From Global Mobility to{' '}
              <span className="text-[#FACC15]">Full Career Migration</span>
            </h1>
            <p className="text-base sm:text-lg lg:text-xl text-slate-300 leading-relaxed max-w-2xl mb-3">
              Your first step in a <strong className="text-white">structured career migration journey</strong>.
            </p>
            <p className="text-sm sm:text-base text-slate-400 leading-relaxed max-w-2xl mb-7">
              Zafco Study Abroad is the student‑focused arm of <strong className="text-white">Zafco Global Mobility</strong> — built on years of experience moving professionals, families, and entrepreneurs across borders. We treat study abroad as a strategic career decision, not a one‑time admission exercise.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/study-abroad/book-counselling/?source=study-abroad-about-zafco-hero"
                className="inline-flex items-center gap-2 bg-[#C5A028] hover:bg-[#EAB308] text-black font-bold text-sm sm:text-base px-7 py-3.5 rounded-full transition-all hover:-translate-y-0.5 shadow-lg shadow-[#C5A028]/30"
                data-testid="az-hero-counselling-cta"
              >
                Start Your Strategic Plan Today <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/study-abroad/visa-comparison/"
                className="inline-flex items-center gap-2 border-2 border-white/30 hover:border-white text-white font-semibold text-sm sm:text-base px-7 py-3.5 rounded-full transition-all"
              >
                See Visa Comparison
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ============ TRUST STRIP: Countries we cover ============ */}
      <section className="bg-slate-50 border-y border-slate-200/60 py-6" data-testid="az-trust-strip">
        <div className="max-w-7xl mx-auto px-6 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-8">
          <p className="text-[10px] uppercase tracking-[0.25em] text-slate-500 font-bold whitespace-nowrap">
            Strategic Pathways Across
          </p>
          <div className="flex items-center gap-3 sm:gap-5 overflow-x-auto no-scrollbar">
            {COUNTRIES.map((c) => (
              <div
                key={c.name}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-slate-200 hover:border-[#C5A028]/40 transition-all whitespace-nowrap"
              >
                <span className="text-base leading-none" aria-hidden="true">{c.flag}</span>
                <span className="text-xs font-semibold text-slate-700">{c.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ 2. CREDIBILITY — MEA trust badge + stats ============ */}
      <section className="bg-white py-14 lg:py-20" data-testid="az-credibility-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-[1.15fr_1fr] gap-8 lg:gap-14 items-center">
            <div>
              <SectionHeading
                eyebrow="Government-Verified Credibility"
                title="An MEA-Approved Foundation, Not Just a Brand Claim."
                subtitle="Zafco Study Abroad operates under Zafco Human Resource Management — duly approved by the Ministry of External Affairs, Government of India, as a licensed Recruitment Agency. That means every process we run, from documentation to visa filing, is built on regulated, audited compliance."
              />
              <div className="grid grid-cols-2 gap-4 sm:gap-5" data-testid="az-credibility-stats">
                {CREDIBILITY_STATS.map((s) => (
                  <div
                    key={s.label}
                    className="rounded-2xl bg-slate-50 border border-slate-200/80 hover:border-[#C5A028]/40 transition-colors p-5 text-center"
                  >
                    <div className="text-2xl sm:text-3xl font-bold font-syne text-[#003087]">{s.value}</div>
                    <div className="text-[11px] sm:text-xs text-slate-500 font-medium mt-1">{s.label}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* MEA Trust Badge card */}
            <div
              className="relative overflow-hidden rounded-3xl text-white p-7 sm:p-9 shadow-xl"
              style={{ background: 'linear-gradient(145deg, #003087 0%, #001F5C 60%, #020617 100%)' }}
              data-testid="az-mea-badge"
            >
              <div aria-hidden="true" className="pointer-events-none absolute -top-16 -right-16 w-56 h-56 rounded-full bg-[#C5A028]/16 blur-3xl" />
              <div className="relative">
                <div className="w-14 h-14 rounded-2xl bg-[#C5A028]/15 border border-[#C5A028]/40 flex items-center justify-center mb-5">
                  <BadgeCheck className="w-7 h-7 text-[#FACC15]" aria-hidden="true" />
                </div>
                <div className="text-[10px] font-bold tracking-[0.25em] text-[#FACC15] uppercase mb-2">
                  Ministry of External Affairs · Govt of India
                </div>
                <h3 className="text-xl sm:text-2xl font-bold font-syne leading-tight mb-3">
                  Licensed Recruitment Agency
                </h3>
                <p className="text-sm text-slate-300 leading-relaxed mb-5">
                  Our registration is public, verifiable, and active — your family can independently confirm our credentials on the Government of India&apos;s eMigrate portal.
                </p>
                <div className="rounded-xl bg-white/8 border border-white/15 px-4 py-3">
                  <div className="text-[9.5px] uppercase tracking-[0.2em] text-slate-400 font-bold mb-1">Registration Certificate No.</div>
                  <div className="text-sm sm:text-base font-mono font-semibold text-white break-all" data-testid="az-mea-rc-number">
                    {MEA_RC}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ 3. LEADERSHIP — 3-column grid ============ */}
      <section className="bg-slate-50 py-14 lg:py-20" data-testid="az-leadership-section">
        <div className="max-w-7xl mx-auto px-6">
          <SectionHeading
            eyebrow="The People Behind Zafco"
            title="Leadership That Has Actually Moved People Across Borders."
            subtitle="Zafco is not run by anonymous counsellors reading scripts. It is led by the same team that built one of North India's most trusted MEA-licensed global mobility firms."
            center
          />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-7" data-testid="az-leadership-grid">
            {LEADERSHIP.map((l, idx) => (
              <article
                key={l.name}
                className="group relative rounded-2xl bg-white border border-slate-200/80 hover:border-[#C5A028]/50 hover:shadow-xl hover:-translate-y-1 transition-all p-7 flex flex-col items-center text-center"
                data-testid={`az-leader-card-${idx}`}
              >
                <div
                  className="w-20 h-20 rounded-full flex items-center justify-center mb-5 text-white text-xl font-bold font-syne tracking-wide shadow-lg shadow-[#003087]/25 ring-4 ring-[#C5A028]/25 group-hover:ring-[#C5A028]/50 transition-all"
                  style={{ background: 'linear-gradient(145deg, #003087 0%, #001F5C 100%)' }}
                  aria-hidden="true"
                >
                  {l.initials}
                </div>
                <h3 className="text-lg font-bold font-syne text-[#003087] leading-tight mb-1">
                  {l.name}
                </h3>
                <div className="inline-flex items-center gap-1.5 text-[11px] font-bold tracking-[0.14em] uppercase text-[#C5A028] mb-4">
                  {l.title}
                </div>
                <p className="text-sm text-slate-600 leading-relaxed">{l.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ============ 4. GEOGRAPHIC FOOTPRINT ============ */}
      <section
        className="relative overflow-hidden py-14 lg:py-20 text-white"
        style={{ background: 'linear-gradient(135deg, #003087 0%, #001F5C 60%, #020617 100%)' }}
        data-testid="az-footprint-section"
      >
        <div aria-hidden="true" className="pointer-events-none absolute -top-24 left-1/3 w-96 h-96 rounded-full bg-[#C5A028]/12 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-6">
          <SectionHeading
            eyebrow="Where We Operate"
            title="A Footprint That Spans Recruitment Corridors and Study Destinations."
            subtitle="Most study abroad agencies know universities. Zafco also knows employers, regulators, and immigration systems — because we operate across them every day."
            light
          />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="az-footprint-grid">
            {FOOTPRINT.map((f, idx) => (
              <article
                key={f.region}
                className="rounded-2xl bg-white/6 border border-white/12 hover:border-[#C5A028]/45 backdrop-blur-sm transition-all p-6 sm:p-7"
                data-testid={`az-footprint-card-${idx}`}
              >
                <div className="w-12 h-12 rounded-xl bg-[#C5A028]/15 border border-[#C5A028]/35 flex items-center justify-center mb-5">
                  <f.Icon className="w-6 h-6 text-[#FACC15]" aria-hidden="true" />
                </div>
                <div className="text-[10px] font-bold tracking-[0.2em] text-[#FACC15] uppercase mb-2">{f.tag}</div>
                <h3 className="text-lg font-bold font-syne leading-tight mb-3">{f.region}</h3>
                <p className="text-sm text-slate-300 leading-relaxed">{f.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ============ 5. CORE PILLARS — 3 cards ============ */}
      <section className="bg-white py-14 lg:py-20">
        <div className="max-w-7xl mx-auto px-6">
          <SectionHeading
            eyebrow="The Zafco Difference"
            title="Where Most Agencies Stop, We Begin."
            subtitle="Where many agencies stop at &lsquo;getting you an offer letter,&rsquo; our core promise is different. Every recommendation is engineered around three non-negotiable pillars."
          />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="az-core-pillars-grid">
            {CORE_PILLARS.map((p, idx) => (
              <article
                key={p.title}
                className="group relative rounded-2xl bg-gradient-to-br from-slate-50 to-white border border-slate-200/80 hover:border-[#C5A028]/50 hover:shadow-xl hover:-translate-y-1 transition-all p-6 sm:p-7 flex flex-col"
                data-testid={`az-pillar-card-${idx}`}
              >
                <div className="w-12 h-12 rounded-xl bg-[#C5A028]/12 border border-[#C5A028]/30 flex items-center justify-center mb-5 group-hover:bg-[#C5A028]/20 transition-colors">
                  <p.Icon className="w-6 h-6 text-[#C5A028]" />
                </div>
                <div className="text-[10px] font-bold tracking-[0.2em] text-slate-400 uppercase mb-2">
                  Pillar 0{idx + 1}
                </div>
                <h3 className="text-lg lg:text-xl font-bold font-syne text-[#003087] mb-3 leading-tight">
                  {p.title}
                </h3>
                <p className="text-sm text-slate-600 leading-relaxed">{p.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ============ 6. THE ZAFCO PROMISE — highlight box ============ */}
      <section className="bg-slate-50 py-14 lg:py-20">
        <div className="max-w-5xl mx-auto px-6">
          <SectionHeading
            eyebrow="Built for Indian Families"
            title="The Zafco Promise."
            subtitle="For Indian students and parents, trust and clarity matter more than slogans. Zafco works on a clear ethical consulting model with three non-negotiables."
          />

          <div
            className="rounded-3xl border-2 border-[#C5A028]/30 bg-gradient-to-br from-amber-50/40 via-white to-amber-50/40 p-6 sm:p-9 shadow-md"
            data-testid="az-promise-box"
          >
            <div className="flex items-center gap-3 mb-6 pb-5 border-b border-amber-200/60">
              <div className="w-11 h-11 rounded-full bg-[#C5A028]/15 border border-[#C5A028]/40 flex items-center justify-center">
                <Handshake className="w-5 h-5 text-[#C5A028]" aria-hidden="true" />
              </div>
              <div>
                <div className="text-[10px] font-bold tracking-[0.2em] text-[#C5A028] uppercase">
                  Our Promise to Every Family
                </div>
                <div className="text-base font-bold font-syne text-[#003087]">
                  The Zafco Consulting Code
                </div>
              </div>
            </div>

            <ul className="space-y-4" data-testid="az-promise-list">
              {PROMISE_POINTS.map((point, idx) => (
                <li
                  key={point}
                  className="flex items-start gap-3"
                  data-testid={`az-promise-point-${idx}`}
                >
                  <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5 shrink-0" aria-hidden="true" />
                  <p className="text-sm sm:text-base text-slate-700 leading-relaxed">{point}</p>
                </li>
              ))}
            </ul>

            <div className="mt-7 pt-5 border-t border-amber-200/60">
              <p className="text-sm sm:text-base text-slate-700 leading-relaxed italic">
                &ldquo;Behind every Zafco file is a single question: will this pathway make sense{' '}
                <strong className="text-[#003087] not-italic">three, five, and ten years from now</strong>
                {' '}— in the job market and in immigration terms?&rdquo;
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ============ FINAL CTA ============ */}
      <section
        className="text-white"
        style={{ background: 'linear-gradient(135deg, #003087 0%, #001F5C 60%, #020617 100%)' }}
      >
        <div className="max-w-4xl mx-auto px-6 py-14 lg:py-20 text-center" data-testid="az-final-cta-section">
          <GraduationCap className="w-12 h-12 text-[#FACC15] mx-auto mb-5" aria-hidden="true" />
          <h2 className="text-2xl md:text-4xl font-bold font-syne mb-4 leading-tight">
            Make your study abroad decision <span className="text-[#FACC15]">strategic</span>, not seasonal.
          </h2>
          <p className="text-slate-300 max-w-2xl mx-auto mb-7 leading-relaxed">
            Our counsellors map your academics, finances, and career intent against the 2026 visa policies of every destination — then build a 3 / 5 / 10-year roadmap, not just an offer letter.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link
              to="/study-abroad/book-counselling/?source=study-abroad-about-zafco-final-cta"
              className="inline-flex items-center justify-center gap-2 bg-[#C5A028] hover:bg-[#EAB308] text-black font-bold px-7 py-3.5 rounded-full transition-all hover:-translate-y-0.5 shadow-lg shadow-[#C5A028]/30"
              data-testid="az-final-counselling-cta"
            >
              Start Your Strategic Plan Today <ArrowUpRight className="w-4 h-4" />
            </Link>
            <Link
              to="/study-abroad/free-profile-evaluation/?source=study-abroad-about-zafco-final-cta"
              className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/15 text-white border border-white/20 font-semibold px-7 py-3.5 rounded-full transition-colors"
              data-testid="az-final-evaluation-cta"
            >
              Get a Free Profile Evaluation
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutZafcoStudyAbroadPage;
